import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Check, X, Loader2, Repeat, Ban, RotateCcw, AlertTriangle, MapPin } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from "@/components/ui/sheet";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import ImageUpload from "@/components/ImageUpload";
import RichTextEditor from "@/components/RichTextEditor";
import RecurrencePicker from "@/components/RecurrencePicker";
import SeriesPatternEditor from "@/components/SeriesPatternEditor";
import ServiceLocationsEditor from "@/components/ServiceLocationsEditor";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
} from "@/components/ui/alert-dialog";

function MultiSelectField({ field, value, onChange }) {
  const selected = Array.isArray(value) ? value : [];
  const isStatic = Array.isArray(field.options);
  const { data: fetched = [] } = useQuery({
    queryKey: ["ms-options", field.optionsEndpoint],
    queryFn: () => api.get(field.optionsEndpoint).then((r) => r.data),
    enabled: !isStatic && !!field.optionsEndpoint,
  });
  const labelKey = field.optionLabel || "title";
  const options = isStatic
    ? field.options.map((o) => ({ id: o, label: o }))
    : (Array.isArray(fetched) ? fetched : []).map((o) => ({ id: o.id, label: o[labelKey] }));
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div data-testid={`field-${field.name}`} className="rounded-lg border border-[#E5E0D8] p-3 bg-[#F9F7F3] max-h-56 overflow-auto space-y-1.5">
      {options.length === 0 && <p className="text-xs text-[#4A5D6B]">No options available.</p>}
      {options.map((o) => (
        <label key={o.id} className="flex items-center gap-2 text-sm text-[#2A3942] cursor-pointer" data-testid={`ms-${field.name}-${o.id}`}>
          <input type="checkbox" checked={selected.includes(o.id)} onChange={() => toggle(o.id)} className="accent-[#144F75]" />
          {o.label}
        </label>
      ))}
      <p className="text-[11px] text-[#8FA89B] pt-1">{selected.length} selected · leave empty to allow all.</p>
    </div>
  );
}

function RelationField({ field, value, onChange }) {
  const labelKey = field.optionLabel || "title";
  const { data: fetched = [] } = useQuery({
    queryKey: ["rel-options", field.optionsEndpoint],
    queryFn: () => api.get(field.optionsEndpoint).then((r) => r.data),
    enabled: !!field.optionsEndpoint,
  });
  const options = (Array.isArray(fetched) ? fetched : []).map((o) => ({ id: field.optionValue ? o[field.optionValue] : o.id, label: o[labelKey] || o.name || o.title }));
  return (
    <select data-testid={`field-${field.name}`} value={value || ""} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-[#E5E0D8] px-3.5 py-2.5 text-sm outline-none focus:border-[#144F75] bg-white">
      <option value="">{field.emptyLabel || "— None —"}</option>
      {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
    </select>
  );
}

const LOC_SPECIAL = { all: "All Locations", virtual: "Virtual / Online" };

// A bookable in-person service should have a room for conflict-blocking.
export const serviceNeedsRoom = (s) =>
  !s.bookable_anytime && !s.room &&
  !(s.service_locations || []).some((l) => l.room || l.mode === "virtual");

function LocationAssignField({ field, value, onChange }) {
  const { data: locs = [] } = useQuery({
    queryKey: ["locations-public"],
    queryFn: () => api.get("/locations").then((r) => r.data),
  });
  return (
    <select data-testid={`field-${field.name}`} required value={value || ""} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-[#E5E0D8] px-3.5 py-2.5 text-sm outline-none focus:border-[#144F75] bg-white">
      <option value="" disabled>— Choose a location —</option>
      {locs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
      <option value="all">All Locations</option>
      <option value="virtual">Virtual / Online</option>
    </select>
  );
}

function DefaultProvidersField({ serviceId, value, onChange }) {
  const dp = value && typeof value === "object" ? value : {};
  const { data } = useQuery({
    queryKey: ["svc-qualified-by-loc", serviceId],
    queryFn: () => api.get(`/admin/services/${serviceId}/qualified-by-location`).then((r) => r.data),
    enabled: !!serviceId,
  });
  if (!serviceId) return <p className="text-xs text-[#8FA89B]">Save the service first, then reopen to set a default provider per location.</p>;
  const locs = data?.locations || [];
  if (locs.length === 0) return <p className="text-xs text-[#8FA89B]">No defaults needed — where only one Enhancer is certified at a location, they're auto-assigned.</p>;
  return (
    <div className="space-y-2 rounded-lg border border-[#E5E0D8] p-3 bg-[#F9F7F3]" data-testid="field-default_providers">
      {locs.map((l) => (
        <div key={l.location_id} className="grid grid-cols-2 gap-2 items-center">
          <span className="text-sm text-[#1A242B] truncate">{l.location_name}</span>
          <select data-testid={`default-provider-${l.location_id}`} value={dp[l.location_id] || ""}
            onChange={(e) => onChange({ ...dp, [l.location_id]: e.target.value })}
            className="rounded-lg border border-[#E5E0D8] px-2.5 py-1.5 text-sm bg-white">
            <option value="">First available (no default)</option>
            {l.providers.map((p) => <option key={p.enhancer_id} value={p.enhancer_id}>{p.name}</option>)}
          </select>
        </div>
      ))}
    </div>
  );
}

function Field({ field, value, onChange, serviceId, startDate }) {
  const base = "w-full rounded-lg border border-[#E5E0D8] px-3.5 py-2.5 text-sm outline-none focus:border-[#144F75] bg-white";
  const common = { "data-testid": `field-${field.name}` };
  if (field.type === "default_providers")
    return <DefaultProvidersField serviceId={serviceId} value={value} onChange={onChange} />;
  if (field.type === "location_assign")
    return <LocationAssignField field={field} value={value} onChange={onChange} />;
  if (field.type === "multiselect")
    return <MultiSelectField field={field} value={value} onChange={onChange} />;
  if (field.type === "relation")
    return <RelationField field={field} value={value} onChange={onChange} />;
  if (field.type === "recurrence")
    return <RecurrencePicker value={value} onChange={onChange} testid={field.name} startDate={startDate} />;
  if (field.type === "event_days") {
    const rows = Array.isArray(value) ? value : [];
    const update = (i, key, v) => onChange(rows.map((r, idx) => idx === i ? { ...r, [key]: v } : r));
    const addRow = () => onChange([...rows, { date: "", start_time: "09:00", end_time: "17:00", label: "" }]);
    const rmRow = (i) => onChange(rows.filter((_, idx) => idx !== i));
    const fld = "rounded-lg border border-[#E5E0D8] px-2.5 py-1.5 text-sm outline-none focus:border-[#144F75] bg-white";
    return (
      <div {...common} className="space-y-2 rounded-lg border border-[#E5E0D8] p-3 bg-[#F9F7F3]">
        {rows.length === 0 && <p className="text-xs text-[#4A5D6B]">Single-day event — add days below only for a multi-day event (retreat, weekend, conference). Each day has its own start/end.</p>}
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_auto] gap-2 items-center" data-testid={`event-days-row-${i}`}>
            <div className="grid grid-cols-3 gap-1.5">
              <input type="date" value={r.date || ""} onChange={(e) => update(i, "date", e.target.value)} data-testid={`event-days-date-${i}`} className={fld} />
              <input type="time" value={r.start_time || ""} onChange={(e) => update(i, "start_time", e.target.value)} data-testid={`event-days-start-${i}`} className={fld} />
              <input type="time" value={r.end_time || ""} onChange={(e) => update(i, "end_time", e.target.value)} data-testid={`event-days-end-${i}`} className={fld} />
            </div>
            <button type="button" onClick={() => rmRow(i)} data-testid={`event-days-remove-${i}`} className="grid place-items-center h-8 w-8 rounded-lg hover:bg-red-50 text-red-600"><X size={15} /></button>
          </div>
        ))}
        <button type="button" onClick={addRow} data-testid="event-days-add-btn" className="text-sm text-[#144F75] font-medium inline-flex items-center gap-1"><Plus size={14} /> Add a day</button>
      </div>
    );
  }

  if (field.type === "service_policy") {
    const p = value && typeof value === "object" ? value : {};
    const upd = (k, v) => {
      const next = { ...p, [k]: v === "" ? "" : Number(v) };
      Object.keys(next).forEach((key) => { if (next[key] === "" || next[key] === null || next[key] === undefined) delete next[key]; });
      onChange(next);
    };
    const fld = "rounded-lg border border-[#E5E0D8] px-2.5 py-1.5 text-sm outline-none focus:border-[#144F75] bg-white w-32 block mt-1";
    return (
      <div {...common} className="space-y-2 rounded-lg border border-[#E5E0D8] p-3 bg-[#F9F7F3]">
        <p className="text-xs text-[#4A5D6B]">Leave blank to inherit the center / global policy. Set a value only when this offering needs <b>stricter</b> terms than the center default.</p>
        <div className="flex flex-wrap gap-5">
          <label className="text-sm text-[#1A242B]">Cancellation window (hours)
            <input type="number" min="0" value={p.cancel_window_hours ?? ""} onChange={(e) => upd("cancel_window_hours", e.target.value)} data-testid="service-policy-cancel-window" placeholder="inherit" className={fld} />
          </label>
          <label className="text-sm text-[#1A242B]">Late cancellation fee ($)
            <input type="number" min="0" step="0.01" value={p.cancel_fee ?? ""} onChange={(e) => upd("cancel_fee", e.target.value)} data-testid="service-policy-cancel-fee" placeholder="inherit" className={fld} />
          </label>
        </div>
      </div>
    );
  }

  if (field.type === "textarea")
    return <textarea {...common} rows={field.rows || 3} value={value || ""} onChange={(e) => onChange(e.target.value)} className={base} />;
  if (field.type === "richtext")
    return <RichTextEditor value={value || ""} onChange={onChange} testid={`field-${field.name}`} placeholder={field.placeholder || "Write…"} media={field.media !== false} />;
  if (field.type === "image")
    return <ImageUpload value={value} onChange={onChange} label="" crop={field.crop || ""} testid={`field-${field.name}`} />;
  if (field.type === "select")
    return (
      <select {...common} value={value || ""} onChange={(e) => onChange(e.target.value)} className={base}>
        {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  if (field.type === "switch")
    return (
      <button type="button" {...common} onClick={() => onChange(!value)}
        className={`relative h-7 w-12 rounded-full transition-colors ${value ? "bg-[#144F75]" : "bg-[#E5E0D8]"}`}>
        <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${value ? "left-6" : "left-1"}`} />
      </button>
    );
  if (field.type === "tiers" || field.type === "priced_options") {
    const rows = Array.isArray(value) ? value : [];
    const isTier = field.type === "tiers";
    const blank = isTier ? { name: "", price: 0, capacity: 0, description: "", closes_on: "" } : { label: "", price: 0, capacity: 0 };
    const update = (i, key, v) => onChange(rows.map((r, idx) => idx === i ? { ...r, [key]: v } : r));
    const addRow = () => onChange([...rows, { ...blank }]);
    const rmRow = (i) => onChange(rows.filter((_, idx) => idx !== i));
    const fld = "rounded-lg border border-[#E5E0D8] px-2.5 py-1.5 text-sm outline-none focus:border-[#144F75] bg-white";
    return (
      <div {...common} className="space-y-2 rounded-lg border border-[#E5E0D8] p-3 bg-[#F9F7F3]">
        {rows.length === 0 && <p className="text-xs text-[#4A5D6B]">{isTier ? "No tiers — event uses the base price above." : "No add-ons."}</p>}
        {rows.map((r, i) => (
          <div key={i} data-testid={`${field.name}-row-${i}`}>
            <div className={`grid gap-2 items-center ${isTier ? "grid-cols-[1fr_5rem_5rem_auto_auto]" : "grid-cols-[1fr_5rem_5rem_auto]"}`}>
              <input placeholder={isTier ? "Tier name (e.g. VIP)" : "Label (e.g. Vegan lunch)"} value={r.name ?? r.label ?? ""} onChange={(e) => update(i, isTier ? "name" : "label", e.target.value)} data-testid={`${field.name}-name-${i}`} className={fld} />
              <input type="number" placeholder="$" title="Price" value={r.price ?? 0} onChange={(e) => update(i, "price", Number(e.target.value))} data-testid={`${field.name}-price-${i}`} className={fld} />
              <input type="number" placeholder="Cap" title="Capacity (0 = unlimited)" value={r.capacity ?? 0} onChange={(e) => update(i, "capacity", Number(e.target.value))} data-testid={`${field.name}-cap-${i}`} className={fld} />
              {isTier && <input type="date" title="Sales close at end of this date (optional — e.g. Early Bird deadline)" value={r.closes_on || ""} onChange={(e) => update(i, "closes_on", e.target.value)} data-testid={`${field.name}-closes-${i}`} className={`${fld} w-[9.5rem]`} />}
              <button type="button" onClick={() => rmRow(i)} data-testid={`${field.name}-remove-${i}`} className="grid place-items-center h-8 w-8 rounded-lg hover:bg-red-50 text-red-600"><X size={15} /></button>
            </div>
            {isTier && <input placeholder="Short description shown to registrants (optional)" value={r.description || ""} onChange={(e) => update(i, "description", e.target.value)} data-testid={`${field.name}-desc-${i}`} className={`${fld} w-full mt-1.5`} />}
          </div>
        ))}
        <button type="button" onClick={addRow} data-testid={`${field.name}-add-btn`} className="text-sm text-[#144F75] font-medium inline-flex items-center gap-1"><Plus size={14} /> Add {isTier ? "tier" : "option"}</button>
      </div>
    );
  }
  if (field.type === "tags")
    return <input {...common} value={Array.isArray(value) ? value.join(", ") : value || ""} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} placeholder="comma, separated" className={base} />;
  if (field.type === "variants") {
    const rows = Array.isArray(value) ? value : [];
    const update = (i, key, v) => onChange(rows.map((r, idx) => idx === i ? { ...r, [key]: v } : r));
    const addRow = () => onChange([...rows, { name: "", price_delta: 0, stock: 0, sku: "" }]);
    const rmRow = (i) => onChange(rows.filter((_, idx) => idx !== i));
    return (
      <div {...common} className="space-y-2 rounded-lg border border-[#E5E0D8] p-3 bg-[#F9F7F3]">
        {rows.length === 0 && <p className="text-xs text-[#4A5D6B]">No variants. Add sizes, scents, or tiers below.</p>}
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_5rem_4.5rem_auto] gap-2 items-center" data-testid={`variant-row-${i}`}>
            <input placeholder="Name (e.g. Large)" value={r.name || ""} onChange={(e) => update(i, "name", e.target.value)} data-testid={`variant-name-${i}`} className="rounded-lg border border-[#E5E0D8] px-2.5 py-1.5 text-sm outline-none focus:border-[#144F75] bg-white" />
            <input type="number" placeholder="+$" title="Price delta ($)" value={r.price_delta ?? 0} onChange={(e) => update(i, "price_delta", Number(e.target.value))} data-testid={`variant-delta-${i}`} className="rounded-lg border border-[#E5E0D8] px-2.5 py-1.5 text-sm outline-none focus:border-[#144F75] bg-white" />
            <input type="number" placeholder="Qty" title="Stock" value={r.stock ?? 0} onChange={(e) => update(i, "stock", Number(e.target.value))} data-testid={`variant-stock-${i}`} className="rounded-lg border border-[#E5E0D8] px-2.5 py-1.5 text-sm outline-none focus:border-[#144F75] bg-white" />
            <button type="button" onClick={() => rmRow(i)} data-testid={`variant-remove-${i}`} className="grid place-items-center h-8 w-8 rounded-lg hover:bg-red-50 text-red-600"><X size={15} /></button>
          </div>
        ))}
        <button type="button" onClick={addRow} data-testid="variant-add-btn" className="text-sm text-[#144F75] font-medium inline-flex items-center gap-1"><Plus size={14} /> Add variant</button>
      </div>
    );
  }
  if (field.type === "number")
    return <input {...common} type="number" value={value ?? 0} onChange={(e) => onChange(Number(e.target.value))} className={base} />;

  const inputType = field.type === "date" ? "date" : field.type === "time" ? "time" : "text";
  return <input {...common} type={inputType} value={value || ""} placeholder={field.placeholder || ""} onChange={(e) => onChange(e.target.value)} className={base} />;
}

export default function ResourceManager({ resourceKey, config }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // record or null(new)
  const [locService, setLocService] = useState(null);
  const [form, setForm] = useState({});
  const [scopePrompt, setScopePrompt] = useState(null); // { action:'save'|'delete', payload }

  const { data: rows = [], isLoading } = useQuery({
    queryKey: [resourceKey, "admin"],
    queryFn: () => api.get(config.endpoint, { params: { published_only: false } }).then((r) => r.data),
  });

  const hasLocCol = config.columns.some((c) => c.type === "location");
  const { data: locList = [] } = useQuery({
    queryKey: ["locations-public"],
    queryFn: () => api.get("/locations").then((r) => r.data),
    enabled: hasLocCol,
  });
  const locName = (v) => LOC_SPECIAL[v] || locList.find((l) => l.id === v)?.name || "Center";
  const missingLoc = hasLocCol ? rows.filter((r) => !r.location_id && !r.is_placeholder) : [];
  const missingRoom = config.roomWarning ? rows.filter((r) => r.published && serviceNeedsRoom(r)) : [];

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: [resourceKey, "admin"] });
    qc.invalidateQueries({ queryKey: [resourceKey] });
  };

  const saveMut = useMutation({
    mutationFn: ({ payload, scope }) =>
      editing?.id
        ? api.put(`${config.endpoint}/${editing.id}${scope ? `?scope=${scope}` : ""}`, payload)
        : api.post(config.endpoint, payload),
    onSuccess: () => {
      toast.success(editing?.id ? "Updated" : "Created");
      setOpen(false);
      setScopePrompt(null);
      invalidate();
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const delMut = useMutation({
    mutationFn: ({ id, scope }) => api.delete(`${config.endpoint}/${id}${scope ? `?scope=${scope}` : ""}`),
    onSuccess: () => { toast.success("Deleted — your changes are live"); setScopePrompt(null); invalidate(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const cancelMut = useMutation({
    mutationFn: ({ id, cancelled }) => api.post(`${config.endpoint}/${id}/${cancelled ? "restore" : "cancel"}-occurrence`),
    onSuccess: (_, v) => { toast.success(v.cancelled ? "Occurrence restored" : "Date canceled — registrants notified"); invalidate(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const openNew = () => { setEditing(null); setForm({ ...config.defaults }); setOpen(true); };
  const openEdit = (row) => {
    setEditing(row);
    const f = {};
    config.fields.forEach((fld) => (f[fld.name] = row[fld.name] ?? config.defaults[fld.name]));
    setForm(f);
    setOpen(true);
  };

  const submit = (e) => {
    e.preventDefault();
    if (editing?.id && editing.series_id) {
      setScopePrompt({ action: "save", payload: form });
      return;
    }
    saveMut.mutate({ payload: form });
  };

  const requestDelete = (row) => {
    if (row.series_id) {
      setScopePrompt({ action: "delete", payload: { id: row.id } });
      return;
    }
    if (window.confirm("Delete this record?")) delMut.mutate({ id: row.id });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-serif text-2xl text-[#1A242B]">{config.label}</h2>
          <p className="text-sm text-[#4A5D6B]">{rows.length} {rows.length === 1 ? "record" : "records"}</p>
        </div>
        {config.noCreate ? (
          <span className="text-xs text-[#8FA89B] max-w-[280px] text-right" data-testid={`admin-no-create-${resourceKey}`}>Created by the owning Center, Enhancer, or Vendor. Admins review, edit, approve, pause &amp; moderate.</span>
        ) : (
          <button data-testid={`admin-new-${resourceKey}-btn`} onClick={openNew} className="eyw-btn !py-2.5 !px-5 text-sm"><Plus size={16} /> New</button>
        )}
      </div>

      {missingLoc.length > 0 && (
        <div data-testid="missing-location-banner" className="mb-5 flex items-start gap-2.5 rounded-xl border border-[#E0A458]/50 bg-[#E0A458]/10 px-4 py-3">
          <AlertTriangle size={16} className="text-[#B5794E] mt-0.5 shrink-0" />
          <p className="text-sm text-[#B5794E]">
            <b>{missingLoc.length} {missingLoc.length === 1 ? "event has" : "events have"} no location assigned.</b> Open each highlighted event and assign a center, All Locations, or Virtual / Online so it appears on the right calendars.
          </p>
        </div>
      )}

      {missingRoom.length > 0 && (
        <div data-testid="missing-room-banner" className="mb-5 flex items-start gap-2.5 rounded-xl border border-[#E0A458]/50 bg-[#E0A458]/10 px-4 py-3">
          <AlertTriangle size={16} className="text-[#B5794E] mt-0.5 shrink-0" />
          <p className="text-sm text-[#B5794E]">
            <b>{missingRoom.length} published {missingRoom.length === 1 ? "service has" : "services have"} no room assigned.</b> Without a room, online bookings can't be conflict-checked and clients may hit scheduling problems. Open each highlighted service and set a room key (or mark it virtual / bookable-anytime).
          </p>
        </div>
      )}

      <div className="hidden md:block rounded-xl border border-[#E5E0D8] bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F9F7F3]">
              {config.columns.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableRow><TableCell colSpan={config.columns.length + 1} className="text-center text-[#4A5D6B] py-8">Loading…</TableCell></TableRow>}
            {rows.map((row) => (
              <TableRow key={row.id} data-testid={`admin-row-${resourceKey}-${row.id}`} className={(hasLocCol && !row.location_id) || (config.roomWarning && row.published && serviceNeedsRoom(row)) ? "bg-[#E0A458]/[0.08]" : ""}>
                {config.columns.map((c, idx) => (
                  <TableCell key={c.key} className="text-[#1A242B]">
                    <span className="inline-flex items-center gap-1.5 flex-wrap">
                      {c.type === "bool"
                        ? (row[c.key] ? <Check size={16} className="text-[#144F75]" /> : <X size={16} className="text-[#4A5D6B]/40" />)
                        : c.type === "location"
                        ? (row[c.key]
                            ? <span data-testid={`admin-loc-${row.id}`}>{locName(row[c.key])}</span>
                            : <span data-testid={`admin-noloc-${row.id}`} className="inline-flex items-center rounded-full bg-red-100 text-red-600 px-2 py-0.5 text-[10px] font-semibold">No location</span>)
                        : String(row[c.key] ?? "—").slice(0, 60)}
                      {idx === 0 && config.roomWarning && row.published && serviceNeedsRoom(row) && (
                        <span data-testid={`admin-noroom-${row.id}`} title="No room assigned — bookings can't be conflict-checked" className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-600 px-2 py-0.5 text-[10px] font-semibold"><AlertTriangle size={10} /> No room</span>
                      )}
                      {idx === 0 && row.series_id && (
                        <span data-testid={`admin-repeats-${row.id}`} className="inline-flex items-center gap-0.5 rounded-full bg-[#144F75]/12 text-[#144F75] px-1.5 py-0.5 text-[10px] font-semibold"><Repeat size={9} /> Repeats</span>
                      )}
                      {idx === 0 && Array.isArray(row.days) && row.days.length > 0 && (
                        <span className="inline-flex items-center rounded-full bg-[#9E5C7F]/15 text-[#9E5C7F] px-1.5 py-0.5 text-[10px] font-semibold">Multi-day</span>
                      )}
                      {idx === 0 && row.cancelled && (
                        <span data-testid={`admin-cancelled-${row.id}`} className="inline-flex items-center rounded-full bg-red-100 text-red-600 px-1.5 py-0.5 text-[10px] font-semibold">Canceled</span>
                      )}
                    </span>
                  </TableCell>
                ))}
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    {!config.readOnly && <button data-testid={`admin-edit-${resourceKey}-btn`} onClick={() => openEdit(row)} className="grid place-items-center h-8 w-8 rounded-lg hover:bg-[#144F75]/10 text-[#144F75]"><Pencil size={15} /></button>}
                    {resourceKey === "events" && (
                      <button data-testid={`admin-skipdate-${resourceKey}-btn`} onClick={() => cancelMut.mutate({ id: row.id, cancelled: !!row.cancelled })} title={row.cancelled ? "Restore this date" : "Cancel/skip this date"} className={`grid place-items-center h-8 w-8 rounded-lg ${row.cancelled ? "hover:bg-green-50 text-[#5C8A72]" : "hover:bg-amber-50 text-[#B5794E]"}`}>{row.cancelled ? <RotateCcw size={15} /> : <Ban size={15} />}</button>
                    )}
                    {!config.readOnly && <button data-testid={`admin-delete-${resourceKey}-btn`} onClick={() => requestDelete(row)} className="grid place-items-center h-8 w-8 rounded-lg hover:bg-red-50 text-red-600"><Trash2 size={15} /></button>}
                    {resourceKey === "services" && <button data-testid={`admin-svc-locations-btn-${row.id}`} onClick={() => setLocService(row)} title="Choose every center and Online where this can be booked" className="grid place-items-center h-8 w-8 rounded-lg hover:bg-[#144F75]/10 text-[#144F75]"><MapPin size={15} /></button>}
                    {config.readOnly && <span className="text-[10px] uppercase tracking-wide text-[#8FA89B] px-2 py-1" data-testid={`admin-readonly-${row.id}`}>Read-only</span>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && rows.length === 0 && <TableRow><TableCell colSpan={config.columns.length + 1} className="text-center text-[#4A5D6B] py-8">No records yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* Mobile: stacked cards (every field + action visible, no horizontal scroll) */}
      <div className="md:hidden space-y-3">
        {isLoading && <p className="text-center text-[#4A5D6B] py-8">Loading…</p>}
        {rows.map((row) => (
          <div key={row.id} data-testid={`admin-card-${resourceKey}-${row.id}`} className="rounded-xl border border-[#E5E0D8] bg-white p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-[#1A242B] flex items-center gap-1.5 flex-wrap">
                  <span className="break-words">{String(row[config.columns[0]?.key] ?? "—")}</span>
                  {row.series_id && <span data-testid={`admin-repeats-${row.id}`} className="inline-flex items-center gap-0.5 rounded-full bg-[#144F75]/12 text-[#144F75] px-1.5 py-0.5 text-[10px] font-semibold"><Repeat size={9} /> Repeats</span>}
                  {Array.isArray(row.days) && row.days.length > 0 && <span className="inline-flex items-center rounded-full bg-[#9E5C7F]/15 text-[#9E5C7F] px-1.5 py-0.5 text-[10px] font-semibold">Multi-day</span>}
                  {row.cancelled && <span className="inline-flex items-center rounded-full bg-red-100 text-red-600 px-1.5 py-0.5 text-[10px] font-semibold">Canceled</span>}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                {resourceKey === "services" && <button data-testid={`admin-svc-locations-mbtn-${row.id}`} onClick={() => setLocService(row)} aria-label="Locations" className="grid place-items-center h-9 w-9 rounded-lg bg-[#144F75]/10 text-[#144F75]"><MapPin size={16} /></button>}
                {!config.readOnly && <button data-testid={`admin-edit-${resourceKey}-btn`} onClick={() => openEdit(row)} className="grid place-items-center h-9 w-9 rounded-lg bg-[#144F75]/10 text-[#144F75]"><Pencil size={16} /></button>}
                {resourceKey === "events" && (
                  <button data-testid={`admin-skipdate-${resourceKey}-btn`} onClick={() => cancelMut.mutate({ id: row.id, cancelled: !!row.cancelled })} title={row.cancelled ? "Restore this date" : "Cancel/skip this date"} className={`grid place-items-center h-9 w-9 rounded-lg ${row.cancelled ? "bg-green-50 text-[#5C8A72]" : "bg-amber-50 text-[#B5794E]"}`}>{row.cancelled ? <RotateCcw size={16} /> : <Ban size={16} />}</button>
                )}
                {!config.readOnly && <button data-testid={`admin-delete-${resourceKey}-btn`} onClick={() => requestDelete(row)} className="grid place-items-center h-9 w-9 rounded-lg bg-red-50 text-red-600"><Trash2 size={16} /></button>}
              </div>
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2">
              {config.columns.slice(1).map((c) => (
                <div key={c.key} className="min-w-0">
                  <dt className="text-[10px] uppercase tracking-wide text-[#8FA89B]">{c.label}</dt>
                  <dd className="text-sm text-[#1A242B] break-words">
                    {c.type === "bool"
                      ? (row[c.key] ? <Check size={15} className="text-[#144F75]" /> : <X size={15} className="text-[#4A5D6B]/40" />)
                      : c.type === "location"
                      ? (row[c.key]
                          ? locName(row[c.key])
                          : <span className="inline-flex items-center rounded-full bg-red-100 text-red-600 px-2 py-0.5 text-[10px] font-semibold">No location</span>)
                      : String(row[c.key] ?? "—")}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
        {!isLoading && rows.length === 0 && <p className="text-center text-[#4A5D6B] py-8 rounded-xl border border-[#E5E0D8] bg-white">No records yet.</p>}
      </div>

      <Dialog open={!!locService} onOpenChange={(o) => { if (!o) { setLocService(null); qc.invalidateQueries(); } }}>
        <DialogContent className="max-w-2xl w-[calc(100vw-1.5rem)] max-h-[85vh] overflow-y-auto overflow-x-hidden" data-testid="admin-svc-locations-dialog">
          <DialogHeader><DialogTitle>Locations — {locService?.title}</DialogTitle></DialogHeader>
          {locService && <ServiceLocationsEditor serviceId={locService.id} testid="admin-svc-locations" />}
        </DialogContent>
      </Dialog>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          data-testid={`admin-sheet-${resourceKey}`}
          className="w-full sm:max-w-lg overflow-y-auto bg-white"
          onPointerDownOutside={(e) => { if (scopePrompt) e.preventDefault(); }}
          onInteractOutside={(e) => { if (scopePrompt) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (scopePrompt) e.preventDefault(); }}
        >
          <SheetHeader>
            <SheetTitle className="font-serif text-2xl text-[#144F75]">{editing?.id ? `Edit ${config.label.replace(/s$/, "")}` : `New ${config.label.replace(/s$/, "")}`}</SheetTitle>
            <SheetDescription>Changes save directly to the ecosystem.</SheetDescription>
          </SheetHeader>
          <form onSubmit={submit} className="mt-6 space-y-4">
            {config.fields.map((f) => (
              (editing?.id && f.type === "recurrence") ? (
                <div key={f.name}>
                  <label className="text-sm font-medium text-[#1A242B] mb-1 block">Repeat pattern</label>
                  <SeriesPatternEditor event={editing} testid="admin-series-pattern" />
                </div>
              ) : (
              <div key={f.name} className={f.type === "switch" ? "flex items-center justify-between" : ""}>
                <label className="text-sm font-medium text-[#1A242B] mb-1 block">{f.label}{f.required && " *"}</label>
                <Field field={f} value={form[f.name]} onChange={(v) => setForm((s) => ({ ...s, [f.name]: v }))} serviceId={editing?.id} startDate={form.date} />
              </div>
              )
            ))}
            <SheetFooter className="pt-4 sticky bottom-0 bg-white -mx-6 px-6 py-4 border-t border-[#E5E0D8] mt-2">
              <button type="button" onClick={() => setOpen(false)} className="eyw-btn-ghost !py-2.5 text-sm">Cancel</button>
              <button data-testid={`admin-save-${resourceKey}-btn`} type="submit" disabled={saveMut.isPending} className="eyw-btn !py-2.5 text-sm disabled:opacity-60">
                {saveMut.isPending && <Loader2 className="animate-spin" size={16} />} Save
              </button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {scopePrompt && (
        <AlertDialog open onOpenChange={(o) => { if (!o) setScopePrompt(null); }}>
          <AlertDialogContent data-testid="series-scope-dialog" className="max-w-sm bg-white">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif text-xl text-[#1A242B]">
                {scopePrompt.action === "delete" ? "Delete recurring event" : "Edit recurring event"}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[#4A5D6B]">
                This event repeats. Apply to:
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-2">
              {[
                { key: "one", label: "This event only" },
                { key: "future", label: "This and following events" },
                { key: "series", label: "All events in the series" },
              ].map((o) => (
                <button
                  key={o.key}
                  data-testid={`scope-${o.key}-btn`}
                  onClick={() =>
                    scopePrompt.action === "delete"
                      ? delMut.mutate({ id: scopePrompt.payload.id, scope: o.key })
                      : saveMut.mutate({ payload: scopePrompt.payload, scope: o.key })
                  }
                  className="w-full text-left rounded-lg border border-[#E5E0D8] px-4 py-2.5 text-sm text-[#1A242B] hover:border-[#144F75] hover:bg-[#144F75]/5 transition-colors"
                >
                  {o.label}
                </button>
              ))}
            </div>
            <button
              data-testid="scope-cancel-btn"
              onClick={() => setScopePrompt(null)}
              className="w-full text-sm text-[#4A5D6B] hover:text-[#144F75]"
            >
              Cancel
            </button>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
